import type { ImageEffect } from './types.js';

/**
 * The photo filters a shopper could apply to their own upload.
 *
 * Customily's image upload has an "apply filters" switch (21 of BearBox's upload
 * fields had it on), and the widget used to show one chip per look. The owner
 * retired those chips (2026-09-28): the widget no longer offers a filter, and
 * `config.applyFilters` is ignored. The list stays because answers saved with a
 * `filter` must keep resolving exactly as they were ordered: the widget preview
 * and the resolver both turn that saved id into scene effects here, and the
 * print worker renders those effects into the production file.
 *
 * A filter is a *preset*, not a stack. Picking Sepia after Black & white must
 * replace it, never layer it — which is why `applyPhotoFilter` strips every
 * filter-owned effect before adding the new one, and why effects the template
 * author put on the layer (a background removal, an outline) survive untouched.
 */

export interface PhotoFilter {
  id: string;
  /** Shown on the chip, in the shop's language. */
  label: string;
  effects: ImageEffect[];
}

export const PHOTO_FILTERS: PhotoFilter[] = [
  { id: 'none', label: 'Original', effects: [] },
  { id: 'bw', label: 'Crno-belo', effects: [{ kind: 'grayscale' }] },
  { id: 'sepia', label: 'Sepia', effects: [{ kind: 'sepia' }] },
  {
    id: 'vivid',
    label: 'Živopisno',
    effects: [
      { kind: 'contrast', amount: 1.18 },
      { kind: 'brightness', amount: 1.05 },
    ],
  },
  {
    id: 'soft',
    label: 'Meko',
    effects: [
      { kind: 'brightness', amount: 1.12 },
      { kind: 'contrast', amount: 0.9 },
    ],
  },
  {
    id: 'ink',
    label: 'Skica',
    // Pure black and white, which is also what a laser engraver wants.
    effects: [{ kind: 'grayscale' }, { kind: 'threshold', level: 0.55 }],
  },
];

/** Effect kinds a filter owns, and may therefore replace. */
const FILTER_KINDS = new Set(
  PHOTO_FILTERS.flatMap((filter) => filter.effects.map((effect) => effect.kind)),
);

export function photoFilter(id: string | undefined): PhotoFilter | undefined {
  if (!id) return undefined;
  return PHOTO_FILTERS.find((filter) => filter.id === id);
}

/**
 * Replaces whatever filter was applied before with this one.
 *
 * Effects the template author authored are kept only when no filter claims that
 * kind — otherwise a layer that ships grayscale would make "Original" impossible
 * to go back to.
 */
export function applyPhotoFilter(
  existing: ImageEffect[] | undefined,
  id: string | undefined,
): ImageEffect[] | undefined {
  const filter = photoFilter(id);
  const kept = (existing ?? []).filter((effect) => !FILTER_KINDS.has(effect.kind));
  const next = filter ? [...kept, ...filter.effects] : kept;
  // An empty array and "no effects" mean the same thing to the renderer; the
  // absent form keeps saved scenes free of noise.
  return next.length > 0 ? next : undefined;
}

/**
 * Honours a shopper who says "use my photo as it is".
 *
 * Cut-outs fail on hair, glasses, pets and busy backgrounds, and when one comes
 * out badly the shopper has no way to say so — the design decided for them.
 * This is that way: it drops the background-removal effect from the layer, which
 * both the live preview and the print worker already read, so one flag turns the
 * cut-out off everywhere rather than only where it happens to be checked.
 *
 * `false` deliberately does nothing rather than adding the effect back: whether
 * a design removes backgrounds at all is the template author's decision, not the
 * shopper's, and a shopper cannot opt IN to a cut-out that was never offered.
 */
export function applyKeepOriginal(
  existing: ImageEffect[] | undefined,
  keepOriginal: boolean | undefined,
): ImageEffect[] | undefined {
  if (!keepOriginal) return existing;
  // Both kinds of cutting are "not the original picture" — one flag turns off
  // whichever the design asked for.
  const kept = (existing ?? []).filter(
    (effect) => effect.kind !== 'removeBackground' && effect.kind !== 'faceCutout',
  );
  return kept.length > 0 ? kept : undefined;
}

/**
 * Everything the shopper decided about their own photo, folded into one list of
 * effects, in one place.
 *
 * There are two such decisions and they are independent — a look to apply, and
 * whether to keep the background the design would otherwise remove — but both
 * end up in the same `effects` array. Applying them as two separate statements
 * is the trap: each one derives its answer from the layer's *authored* effects,
 * so the second silently discards the first, and the shopper who asked to keep
 * their background and picked sepia gets a sepia cut-out.
 *
 * They are composed here rather than at each call site because there are two
 * call sites — the browser preview and the server resolver — and a composition
 * order that differs between them is a preview that lies.
 */
export function shopperEffects(
  authored: ImageEffect[] | undefined,
  choices: { filter?: unknown; keepOriginal?: unknown },
): ImageEffect[] | undefined {
  let effects = authored;
  if ('filter' in choices) {
    effects = applyPhotoFilter(
      effects,
      typeof choices.filter === 'string' ? choices.filter : undefined,
    );
  }
  if (choices.keepOriginal) effects = applyKeepOriginal(effects, true);
  return effects;
}
